/**
 * TaxEmailResponseUploader (TAX-EMAIL-MULTIUPLOAD-001) — premium drag & drop zone + selected-file cards for the
 * Tax Collector response documents. Limits mirror the backend gate: 5 files · 5 MB each · 25 MB total.
 */
import React, { useRef, useState } from 'react';
import { CloudUpload, FileText, FileImage, FileSpreadsheet, FileType, Mail, File as FileIcon, CheckCircle2, XCircle, Trash2, X, Loader2, Upload, Sparkles } from 'lucide-react';

export const ACCEPT = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.tif,.tiff,.eml,.msg';
export const MAX_FILES = 5;
export const MAX_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
const EXTS = ACCEPT.split(',');

export const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const extOf = (name) => (name.includes('.') ? `.${name.split('.').pop().toLowerCase()}` : '');

const KIND = {
  '.pdf': { label: 'PDF', Icon: FileText, cls: 'bg-rose-500 text-white' },
  '.jpg': { label: 'JPG', Icon: FileImage, cls: 'bg-sky-500 text-white' },
  '.jpeg': { label: 'JPG', Icon: FileImage, cls: 'bg-sky-500 text-white' },
  '.png': { label: 'PNG', Icon: FileImage, cls: 'bg-sky-500 text-white' },
  '.tif': { label: 'TIF', Icon: FileImage, cls: 'bg-sky-500 text-white' },
  '.tiff': { label: 'TIF', Icon: FileImage, cls: 'bg-sky-500 text-white' },
  '.doc': { label: 'W', Icon: FileType, cls: 'bg-blue-600 text-white' },
  '.docx': { label: 'W', Icon: FileType, cls: 'bg-blue-600 text-white' },
  '.xls': { label: 'X', Icon: FileSpreadsheet, cls: 'bg-emerald-600 text-white' },
  '.xlsx': { label: 'X', Icon: FileSpreadsheet, cls: 'bg-emerald-600 text-white' },
  '.eml': { label: 'EML', Icon: Mail, cls: 'bg-slate-500 text-white' },
  '.msg': { label: 'MSG', Icon: Mail, cls: 'bg-slate-500 text-white' },
};
export const kindOf = (name) => KIND[extOf(name || '')] || { label: 'FILE', Icon: FileIcon, cls: 'bg-slate-400 text-white' };

export const FileBadge = ({ name, size = 'md' }) => {
  const k = kindOf(name);
  const dim = size === 'sm' ? 'w-7 h-7 rounded-md' : 'w-10 h-10 rounded-lg';
  return (
    <span className={`inline-flex items-center justify-center ${dim} ${k.cls} shadow-sm flex-shrink-0`} data-testid="tax-email-file-badge">
      <k.Icon className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5'} />
    </span>
  );
};

/** Pure client-side pre-check (the server re-validates everything). Returns {ok, error} per file. */
export const validateSelection = (existing, incoming) => {
  const accepted = [...existing];
  const rejected = [];
  for (const f of incoming) {
    const ext = extOf(f.name);
    const dup = accepted.some((e) => e.name.toLowerCase() === f.name.toLowerCase() && e.size === f.size);
    if (!EXTS.includes(ext)) rejected.push({ name: f.name, reason: 'Unsupported file type' });
    else if (f.size === 0) rejected.push({ name: f.name, reason: 'File is empty' });
    else if (f.size > MAX_FILE_BYTES) rejected.push({ name: f.name, reason: `Larger than 5 MB (${fmtSize(f.size)})` });
    else if (dup) rejected.push({ name: f.name, reason: 'Already selected' });
    else if (accepted.length >= MAX_FILES) rejected.push({ name: f.name, reason: `Maximum ${MAX_FILES} files per submission` });
    else if (accepted.reduce((s, e) => s + e.size, 0) + f.size > MAX_TOTAL_BYTES) rejected.push({ name: f.name, reason: 'Total would exceed 25 MB' });
    else accepted.push(f);
  }
  return { accepted, rejected };
};

const Deco = ({ className, children }) => (
  <span className={`absolute hidden lg:flex items-center justify-center rounded-xl shadow-md pointer-events-none select-none ${className}`} aria-hidden="true">{children}</span>
);

export function DropZone({ disabled, onFiles, count }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const open = () => { if (!disabled) inputRef.current?.click(); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setOver(false);
    if (disabled) return;
    onFiles(Array.from(e.dataTransfer?.files || []));
  };
  const full = count >= MAX_FILES;
  return (
    <div role="button" tabIndex={0} onClick={open} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && open()}
      onDragEnter={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
      onDrop={onDrop}
      data-testid="tax-email-dropzone" data-dragover={over ? 'true' : 'false'}
      className={`relative overflow-hidden rounded-2xl border-2 border-dashed px-6 py-8 text-center cursor-pointer outline-none
        transition-[transform,background-color,border-color,box-shadow] duration-200 ease-out
        focus-visible:ring-2 focus-visible:ring-blue-400
        ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
        ${over ? 'border-blue-500 bg-blue-100/70 scale-[1.01] shadow-[0_0_0_6px_rgba(59,130,246,0.12)]'
               : 'border-blue-300 bg-gradient-to-b from-sky-50 to-blue-50/40 hover:border-blue-400 hover:bg-blue-50/70'}`}>
      <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" disabled={disabled} data-testid="tax-email-received-file-input"
        onChange={(e) => { onFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
      <Deco className="left-[6%] top-[52%] w-12 h-12 bg-rose-500 text-white -rotate-6"><FileText className="w-6 h-6" /><span className="absolute -bottom-1 text-[9px] font-bold tracking-wide bg-rose-600 px-1 rounded">PDF</span></Deco>
      <Deco className="left-[11%] top-[28%] w-12 h-12 bg-sky-500 text-white rotate-3 text-base font-bold">W</Deco>
      <Deco className="left-[17%] top-[55%] w-12 h-12 bg-emerald-500 text-white -rotate-3 text-base font-bold">X</Deco>
      <Deco className="left-[22%] top-[30%] w-12 h-12 bg-sky-400 text-white rotate-6"><FileImage className="w-6 h-6" /></Deco>
      <Deco className="right-[8%] top-[22%] w-24 h-28 bg-white/90 border border-blue-100 rotate-3 flex-col gap-1.5 p-3">
        {[80, 60, 70, 50].map((w, i) => <span key={i} className="h-1.5 rounded bg-blue-100" style={{ width: `${w}%` }} />)}
      </Deco>
      <Deco className="right-[5%] top-[58%] w-14 h-14 rounded-full bg-sky-400 text-white"><Upload className="w-7 h-7" /></Deco>
      <Sparkles className="absolute hidden lg:block right-[21%] top-[30%] w-5 h-5 text-blue-400" aria-hidden="true" />
      <Sparkles className="absolute hidden lg:block right-[3%] top-[28%] w-4 h-4 text-sky-300" aria-hidden="true" />

      <div className="relative z-10 flex flex-col items-center gap-2">
        <CloudUpload className={`w-14 h-14 text-blue-600 transition-transform duration-300 ${over ? '-translate-y-1 scale-110' : ''}`} strokeWidth={1.6} />
        <p className="text-lg md:text-xl font-semibold text-slate-800" data-testid="tax-email-dropzone-title">
          {over ? 'Release to add the files' : 'Drag & drop response documents here'}
        </p>
        <span className="text-sm text-slate-500">or</span>
        <button type="button" onClick={(e) => { e.stopPropagation(); open(); }} disabled={disabled || full} data-testid="tax-email-browse-btn"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white text-sm font-semibold shadow-md transition-[background-color,transform] disabled:opacity-50">
          <FileText className="w-4 h-4" /> Browse Files
        </button>
        <p className="mt-1 text-sm text-slate-600">Supported formats: PDF, JPG, JPEG, PNG, DOC, DOCX, XLS, XLSX, TIF, EML, MSG</p>
        <p className="text-xs font-medium text-slate-500" data-testid="tax-email-dropzone-limits">
          You can select multiple files at once · Maximum {MAX_FILES} files · 5 MB each · 25 MB total
        </p>
      </div>
    </div>
  );
}

const STATE = {
  ready: { text: 'Ready to upload', cls: 'text-emerald-600', Icon: CheckCircle2 },
  uploading: { text: 'Uploading…', cls: 'text-blue-600', Icon: Loader2, spin: true },
  done: { text: 'Uploaded', cls: 'text-emerald-600', Icon: CheckCircle2, pop: true },
  failed: { text: 'Failed', cls: 'text-rose-600', Icon: XCircle },
};

export function FileCard({ file, state = 'ready', progress, failedReason, onRemove, disabled }) {
  const s = STATE[state] || STATE.ready;
  return (
    <div className={`relative flex items-center gap-3 rounded-xl border bg-white px-3 py-2.5 shadow-sm overflow-hidden
        transition-[border-color,box-shadow,opacity] duration-200 animate-in fade-in slide-in-from-bottom-1
        ${state === 'failed' ? 'border-rose-300' : state === 'done' ? 'border-emerald-300' : 'border-slate-200 hover:shadow-md'}`}
      data-testid="tax-email-file-card" data-state={state}>
      <FileBadge name={file.name} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 truncate" title={file.name} data-testid="tax-email-file-name">{file.name}</p>
        <p className="text-xs text-slate-500" data-testid="tax-email-file-size">{fmtSize(file.size)}{failedReason ? <span className="text-rose-600"> · {failedReason}</span> : null}</p>
      </div>
      <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${s.cls} ${s.pop ? 'animate-in zoom-in-50 duration-300' : ''}`} data-testid="tax-email-file-status">
        <s.Icon className={`w-4 h-4 ${s.spin ? 'animate-spin' : ''}`} /> {s.text}{state === 'uploading' && typeof progress === 'number' ? ` ${progress}%` : ''}
      </span>
      {onRemove && (
        <button type="button" onClick={onRemove} disabled={disabled} aria-label={`Remove ${file.name}`} data-testid="tax-email-file-remove-btn"
          className="ml-1 inline-flex items-center justify-center w-8 h-8 rounded-md border border-slate-200 text-slate-500 hover:text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors disabled:opacity-40">
          <X className="w-4 h-4" />
        </button>
      )}
      {state === 'uploading' && (
        <span className="absolute left-0 bottom-0 h-0.5 bg-blue-500 transition-[width] duration-200" style={{ width: `${progress || 0}%` }} />
      )}
    </div>
  );
}

export const ClearAllButton = ({ onClick, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid="tax-email-clear-all-btn"
    className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-40 transition-colors">
    <Trash2 className="w-4 h-4" /> Clear All
  </button>
);
